import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DbShift, ShiftInsert, ShiftUpdate } from "@/types/rota";
import { useToast } from "@/hooks/use-toast";
import { useNurseryId } from "@/hooks/useNurseryId";
import { logAudit } from "@/lib/audit";
import { friendlyError } from "@/lib/friendlyError";

export function useShifts(weekStart: string, weekEnd: string) {
  const nurseryId = useNurseryId();
  return useQuery({
    queryKey: ["shifts", nurseryId, weekStart, weekEnd],
    enabled: !!nurseryId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shifts")
        .select("*")
        .gte("date", weekStart)
        .lte("date", weekEnd)
        .order("start_time");
      if (error) throw error;
      return data as DbShift[];
    },
  });
}

export function useAddShift() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (shift: ShiftInsert) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: profile } = await supabase
        .from("profiles")
        .select("nursery_id")
        .eq("user_id", user.id)
        .single();
      if (!profile?.nursery_id) throw new Error("No nursery found");
      const { data, error } = await supabase.from("shifts").insert({ ...shift, nursery_id: profile.nursery_id } as any).select().single();
      if (error) throw error;
      return data as DbShift;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      toast({ title: "Shift added ✅" });
      logAudit({ description: `Added shift on ${data.date} (${data.start_time}-${data.end_time})`, recordType: "shift", recordId: data.id });
    },
    onError: (e: Error) => { toast({ title: "Failed to add shift", description: friendlyError(e), variant: "destructive" }); },
  });
}

export function useUpdateShift() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ShiftUpdate }) => {
      const { data, error } = await supabase.from("shifts").update(updates as any).eq("id", id).select().single();
      if (error) throw error;
      return data as DbShift;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      toast({ title: "Shift updated ✅" });
      logAudit({ description: `Edited shift on ${data.date}`, recordType: "shift", recordId: data.id });
    },
    onError: (e: Error) => { toast({ title: "Could not save your changes", description: friendlyError(e), variant: "destructive" }); },
  });
}

export function useDeleteShift() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("shifts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      toast({ title: "Shift deleted 🗑️" });
      logAudit({ description: `Deleted shift`, recordType: "shift", recordId: id });
    },
    onError: (e: Error) => { toast({ title: "Could not remove that", description: friendlyError(e), variant: "destructive" }); },
  });
}
